import React, { useRef, useState } from 'react';
import { Alert, Button, Input, Select, Tooltip, message } from 'antd';
import {
  CloseOutlined,
  FolderOpenOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type {
  AiGenerateCapability,
  GenerateParams,
  ImageQuality,
  InputImageRecord,
} from '../../../types/aiGenerate';
import { COUNT_OPTIONS, QUALITY_OPTIONS, SIZE_OPTIONS } from '../../../services/aiGenerate';
import { uploadFileAndGetRecord } from '../../../helpers/file';
import ImageSelector, { type ImageSelectorRecord } from '../../ImageSelector';
import { useTranslation } from '../../../hooks/useTranslation';
import './index.css';

const { TextArea } = Input;

interface InputPanelProps {
  images: InputImageRecord[];
  text: string;
  params: GenerateParams;
  capability: AiGenerateCapability | null;
  generating: boolean;
  onImagesChange: (images: InputImageRecord[]) => void;
  onTextChange: (text: string) => void;
  onParamsChange: (params: GenerateParams) => void;
  onGenerate: () => void;
}

const InputPanel: React.FC<InputPanelProps> = ({
  images,
  text,
  params,
  capability,
  generating,
  onImagesChange,
  onTextChange,
  onParamsChange,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const [showImageSelector, setShowImageSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const maxInputs = capability?.maxInputImages || 4;
  const maxOutputs = capability?.maxOutputsPerTask || 4;
  const inputCount = Math.max(1, images.length);
  const plannedOutputs = inputCount * params.count;
  const availableCounts = COUNT_OPTIONS.filter((count) => inputCount * count <= maxOutputs);
  const blocked = !capability?.enabled || !capability.eligible;
  const insufficientQuota = Boolean(capability && plannedOutputs > capability.remaining);

  const updateParams = <K extends keyof GenerateParams>(key: K, value: GenerateParams[K]) => {
    onParamsChange({ ...params, [key]: value });
  };

  const handleRemoveImage = (index: number) => {
    onImagesChange(images.filter((_, imageIndex) => imageIndex !== index));
  };

  const mergeRecords = (selected: InputImageRecord[]) => {
    const existing = new Set(images.map((image) => image.id));
    const additions = selected.filter((image) => !existing.has(image.id));
    if (images.length + additions.length > maxInputs) {
      message.warning(t('aiGenerate.maxInputImages', { count: maxInputs }));
      return;
    }
    onImagesChange([...images, ...additions]);
  };

  const handleSelectRecords = (records: ImageSelectorRecord[]) => {
    mergeRecords(records.map((record) => ({
      id: record.id,
      url: record.url,
      fileName: record.fileName,
      contentType: record.contentType,
    })));
  };

  const uploadLocalFile = async (file: File): Promise<InputImageRecord | null> => {
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      message.error(t('aiGenerate.pleaseUploadPngOrJpeg'));
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error(t('aiGenerate.imageSizeLimit'));
      return null;
    }
    try {
      message.loading({ content: t('aiGenerate.uploadingImage'), key: 'ai-generate-upload' });
      const record = await uploadFileAndGetRecord(file, 'ai-generate');
      return record;
    } catch (error) {
      const reason = error instanceof Error ? error.message : t('common.unknown');
      message.error({
        content: t('aiGenerate.uploadFailedWithMessage', { message: reason }),
        key: 'ai-generate-upload',
      });
      return null;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    const availableSlots = Math.max(0, maxInputs - images.length);
    if (files.length > availableSlots) {
      message.warning(t('aiGenerate.maxInputImages', { count: maxInputs }));
    }
    const uploaded = await Promise.all(files.slice(0, availableSlots).map(uploadLocalFile));
    const records = uploaded.filter((record): record is InputImageRecord => record !== null);
    if (records.length > 0) {
      mergeRecords(records);
      message.success({ content: t('aiGenerate.uploadImageSuccess'), key: 'ai-generate-upload' });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!blocked && !insufficientQuota && text.trim() && !generating) {
        onGenerate();
      }
    }
  };

  return (
    <div className="input-panel">
      {capability && (!capability.enabled || !capability.eligible) && (
        <Alert
          type="warning"
          showIcon
          message={capability.enabled
            ? t('aiGenerate.vipRequired')
            : t('aiGenerate.featureUnavailable')}
          style={{ marginBottom: 12 }}
        />
      )}

      {images.length > 0 && (
        <div className="input-images-preview">
          {images.map((image, index) => (
            <div key={image.id} className="preview-image-item">
              <img src={image.url} alt={image.fileName || `图片 ${index + 1}`} />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                className="remove-image-btn"
                onClick={() => handleRemoveImage(index)}
              />
              <div className="preview-image-name">{image.fileName || `图片 ${index + 1}`}</div>
            </div>
          ))}
        </div>
      )}

      <div className="input-params">
        <div className="param-item">
          <span className="param-label">
            {t('aiGenerate.model')}:
            <Tooltip title={t('aiGenerate.serverManagedModel')}>
              <QuestionCircleOutlined className="param-help" />
            </Tooltip>
          </span>
          <Select
            value={capability?.model || params.model}
            size="small"
            disabled
            style={{ width: 140 }}
            options={[{ value: capability?.model || params.model, label: 'GPT Image 2' }]}
          />
        </div>

        <div className="param-item">
          <span className="param-label">{t('aiGenerate.generateCount')}:</span>
          <Select
            value={params.count}
            onChange={(value) => updateParams('count', value)}
            size="small"
            style={{ width: 80 }}
            options={availableCounts.map((count) => ({ value: count, label: `${count}张` }))}
          />
        </div>

        <div className="param-item">
          <span className="param-label">{t('aiGenerate.imageSize')}:</span>
          <Select
            value={params.size}
            onChange={(value) => updateParams('size', value)}
            size="small"
            style={{ width: 170 }}
            options={SIZE_OPTIONS.filter((option) => !capability || capability.sizes.includes(option.value))}
          />
        </div>

        <div className="param-item">
          <span className="param-label">{t('aiGenerate.quality')}:</span>
          <Select
            value={params.quality}
            onChange={(value: ImageQuality) => updateParams('quality', value)}
            size="small"
            style={{ width: 100 }}
            options={QUALITY_OPTIONS.filter((option) => !capability || capability.qualities.includes(option.value as ImageQuality))}
          />
        </div>
      </div>

      <div className="input-hint">
        {t('aiGenerate.quotaHint', {
          planned: plannedOutputs,
          remaining: capability?.remaining ?? 0,
          limit: capability?.dailyLimit ?? 4,
        })}
      </div>

      <div className="input-area">
        <div className="input-actions">
          <Tooltip title={t('aiGenerate.uploadLocalImage')}>
            <Button
              type="text"
              icon={<UploadOutlined />}
              disabled={blocked || images.length >= maxInputs}
              onClick={() => fileInputRef.current?.click()}
            />
          </Tooltip>
          <Tooltip title={t('aiGenerate.selectFromHistory')}>
            <Button
              type="text"
              icon={<FolderOpenOutlined />}
              disabled={blocked || images.length >= maxInputs}
              onClick={() => setShowImageSelector(true)}
            />
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        <div className="input-textarea-wrapper">
          <TextArea
            value={text}
            onChange={(event) => onTextChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('aiGenerate.promptPlaceholder')}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={2000}
            showCount
            disabled={blocked}
            className="input-textarea"
          />
        </div>

        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={onGenerate}
          loading={generating}
          disabled={generating || blocked || insufficientQuota || !text.trim()}
          className="send-btn"
        />
      </div>

      <ImageSelector
        open={showImageSelector}
        onClose={() => setShowImageSelector(false)}
        onSelect={() => undefined}
        onSelectRecords={handleSelectRecords}
        acceptedContentTypes={['image/png', 'image/jpeg']}
        currentImages={images.map((image) => image.url)}
      />
    </div>
  );
};

export default InputPanel;
